import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/SubaccountsFranchises_CriarSubcontaInput.ts';
import '../contracts/SubaccountsFranchises_AtualizarInput.ts';
import '../contracts/SubaccountsFranchises_ConfigurarWebhookInput.ts';

import type { SubaccountsFranchises_DetalheResponseDados } from '../contracts/SubaccountsFranchises_DetalheResponse';
import '../contracts/SubaccountsFranchises_AtualizarResponse';
import '../contracts/SubaccountsFranchises_AtualizarConfigResponse';
import '../contracts/SubaccountsFranchises_BalanceResponse';
import '../contracts/SubaccountsFranchises_ByFranchiseResponse';
import '../contracts/SubaccountsFranchises_ConfigurarWebhookResponse';
import '../contracts/SubaccountsFranchises_CriarSubcontaResponse';
import '../contracts/SubaccountsFranchises_DeletarResponse';
import '../contracts/SubaccountsFranchises_DetalheResponse';
import '../contracts/SubaccountsFranchises_FiscalConfigResponse';
import '../contracts/SubaccountsFranchises_FranchisesWithSubaccountsResponse';
import '../contracts/SubaccountsFranchises_SyncResponse';
import '../contracts/SubaccountsFranchises_WebhookStatusResponse';
import type { SubaccountsFranchises_AtualizarConfigInput } from '../contracts/SubaccountsFranchises_AtualizarConfigInput';
import type { SubaccountsFranchises_AtualizarInput } from '../contracts/SubaccountsFranchises_AtualizarInput';
import type { SubaccountsFranchises_ConfigurarWebhookInput } from '../contracts/SubaccountsFranchises_ConfigurarWebhookInput';
import type { SubaccountsFranchises_CriarSubcontaInput } from '../contracts/SubaccountsFranchises_CriarSubcontaInput';
import type { SubaccountsFranchises_FiscalConfigInput } from '../contracts/SubaccountsFranchises_FiscalConfigInput';
/**
 * Composable for managing Payment Subaccounts (Franchises)
 *
 * ARCHITECTURE:
 * =============
 * Subaccounts are payment accounts created under the Master Account.
 * They are stored in the `payment_accounts` table and linked to franchises
 * via the `payment_account_franchises` junction table.
 *
 * Relationships:
 * - One subaccount can serve multiple franchises
 * - Each franchise links to exactly one subaccount
 *
 * Each subaccount has:
 * - Its own wallet_id (returned by provider when created)
 * - Its own api_key (for direct API calls)
 * - Configuration for payments, notifications, etc.
 * - Fiscal configuration for invoice generation
 */
export function useFranchisesSubaccounts() {
  const isLoading = ref(false);
  const error = ref(null);

  // ===========================================================================
  // CRUD OPERATIONS
  // ===========================================================================

  /**
   * Create a new subaccount via master account
   * This is the primary method for creating new payment accounts for franchises.
   *
   * @param {Object} data - Subaccount data
   * @param {string} data.name - Account holder name
   * @param {string} data.email - Account email
   * @param {string} data.document - CPF/CNPJ
   * @param {string} data.phone - Phone number (optional)
   * @param {number[]} data.franchise_ids - Franchise IDs to link
   * @param {string} data.provider_code - Provider code (default: 'asaas')
   * @returns {Object} Created subaccount with provider response
   */
  const create = async (data: SubaccountsFranchises_CriarSubcontaInput) => {
    log('useFranchisesSubaccounts', 'create', 'Creating subaccount via master', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('SubaccountsFranchises_CriarSubcontaInput', data);
      const response = await httpService.post('/index.php/subaccounts/franchises/criar_subconta', data, { responseContract: 'SubaccountsFranchises_CriarSubcontaResponse' });
      log('useFranchisesSubaccounts', 'create', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar subconta');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'create', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List all subaccounts
   * @param {Object} filters - Optional filters
   * @param {number} filters.franchise_id - Filter by franchise
   * @returns {Array} Array of subaccount objects
   */
  const list = async (filters: any = {}) => {
    log('useFranchisesSubaccounts', 'list', 'Fetching subaccounts', filters);
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if (filters.franchise_id) params.append('franchise_id', filters.franchise_id);

      const queryString = params.toString();
      const url = `/index.php/subaccounts/franchises/lista${queryString ? '?' + queryString : ''}`;

      const response = await httpService.get(url, { responseContract: 'SubaccountsFranchises_ListaResponse' });
      log('useFranchisesSubaccounts', 'list', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao listar subcontas');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'list', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get subaccount by ID
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Subaccount details with linked franchises
   */
  // Detalhe contract is shape-erased (unknown[]) in PHP — actual payload is a single record
  type SubaccountDetalhe = { config?: Record<string, unknown>; [k: string]: unknown };

  const getById = async (subaccountId: number): Promise<SubaccountDetalhe | undefined> => {
    log('useFranchisesSubaccounts', 'getById', 'Fetching subaccount', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/franchises/detalhe/${subaccountId}`, { responseContract: 'SubaccountsFranchises_DetalheResponse' });
      log('useFranchisesSubaccounts', 'getById', 'Response', response);

      if (response.success) {
        return response.dados as unknown as SubaccountDetalhe;
      } else {
        throw new Error(response.error || 'Subconta nao encontrada');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'getById', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get subaccount for a specific franchise
   * Each franchise can only be linked to one subaccount.
   *
   * @param {number} franchiseId - Franchise ID
   * @returns {Object|null} Subaccount or null if not found
   */
  const getByFranchise = async (franchiseId: number) => {
    log('useFranchisesSubaccounts', 'getByFranchise', 'Fetching subaccount for franchise', franchiseId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/franchises/by_franchise/${franchiseId}`, { responseContract: 'SubaccountsFranchises_ByFranchiseResponse' });
      log('useFranchisesSubaccounts', 'getByFranchise', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        return null;
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'getByFranchise', 'Error', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update subaccount
   * @param {number} subaccountId - Subaccount ID
   * @param {Object} data - Update data
   * @param {string} data.name - Account name
   * @param {number[]} data.franchise_ids - Franchise IDs to link (replaces existing)
   * @returns {Object} Updated subaccount
   */
  const update = async (subaccountId: any, data: SubaccountsFranchises_AtualizarInput) => {
    log('useFranchisesSubaccounts', 'update', 'Updating subaccount', { subaccountId, data });
    isLoading.value = true;
    error.value = null;

    try {
      validate('SubaccountsFranchises_AtualizarInput', data);
      const response = await httpService.put(`/index.php/subaccounts/franchises/atualizar/${subaccountId}`, data, { responseContract: 'SubaccountsFranchises_AtualizarResponse' });
      log('useFranchisesSubaccounts', 'update', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar subconta');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'update', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete subaccount (soft delete)
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Deletion result
   */
  const remove = async (subaccountId: number) => {
    log('useFranchisesSubaccounts', 'remove', 'Deleting subaccount', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.delete(`/index.php/subaccounts/franchises/deletar/${subaccountId}`, { responseContract: 'SubaccountsFranchises_DeletarResponse' });
      log('useFranchisesSubaccounts', 'remove', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao excluir subconta');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'remove', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // BALANCE & SYNC OPERATIONS
  // ===========================================================================

  /**
   * Get subaccount balance
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Balance information
   */
  const getBalance = async (subaccountId: number) => {
    log('useFranchisesSubaccounts', 'getBalance', 'Fetching balance', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/subaccounts/franchises/balance/${subaccountId}`, { responseContract: 'SubaccountsFranchises_BalanceResponse' });
      log('useFranchisesSubaccounts', 'getBalance', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar saldo');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'getBalance', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Sync subaccount with provider
   * Updates local data with provider's current state
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Sync result with updated data
   */
  const sync = async (subaccountId: number) => {
    log('useFranchisesSubaccounts', 'sync', 'Syncing subaccount', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/subaccounts/franchises/sync/${subaccountId}`, null, { inputContract: 'SubaccountsFranchises_SyncInput', responseContract: 'SubaccountsFranchises_SyncResponse' });
      log('useFranchisesSubaccounts', 'sync', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao sincronizar subconta');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'sync', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // CONFIGURATION OPERATIONS
  // ===========================================================================

  /**
   * Update subaccount configuration (payment settings, notifications, etc.)
   * @param {number} subaccountId - Subaccount ID
   * @param {Object} config - Configuration object
   * @returns {Object} Updated subaccount
   */
  const updateConfig = async (subaccountId: any, config: SubaccountsFranchises_AtualizarConfigInput['config']) => {
    log('useFranchisesSubaccounts', 'updateConfig', 'Updating config', { subaccountId, config });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.put(`/index.php/subaccounts/franchises/atualizar_config/${subaccountId}`, { config }, { responseContract: 'SubaccountsFranchises_AtualizarConfigResponse', inputContract: 'SubaccountsFranchises_AtualizarConfigInput' });
      log('useFranchisesSubaccounts', 'updateConfig', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar configuracao');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'updateConfig', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update fiscal configuration (NFS-e settings)
   * @param {number} subaccountId - Subaccount ID
   * @param {Object} fiscalConfig - Fiscal configuration
   * @returns {Object} Updated subaccount
   */
  const updateFiscalConfig = async (subaccountId: any, fiscalConfig: SubaccountsFranchises_FiscalConfigInput) => {
    log('useFranchisesSubaccounts', 'updateFiscalConfig', 'Updating fiscal config', { subaccountId, fiscalConfig });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.put(`/index.php/subaccounts/franchises/fiscal_config/${subaccountId}`, fiscalConfig, { responseContract: 'SubaccountsFranchises_FiscalConfigResponse', inputContract: 'SubaccountsFranchises_FiscalConfigInput' });
      log('useFranchisesSubaccounts', 'updateFiscalConfig', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar configuracao fiscal');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'updateFiscalConfig', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get subaccount configuration
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Configuration object
   */
  const getConfig = async (subaccountId: number) => {
    const subaccount = await getById(subaccountId);
    return subaccount?.config || {};
  };

  // ===========================================================================
  // WEBHOOK OPERATIONS
  // ===========================================================================

  /**
   * Configure webhook for a subaccount
   *
   * This creates/updates the webhook configuration on the payment provider.
   * Webhooks are essential for receiving payment status updates.
   *
   * @param {number} subaccountId - Subaccount ID
   * @param {Object} config - Webhook configuration
   * @param {Array} config.events - Events to subscribe to (optional, uses defaults)
   * @param {boolean} config.enabled - Enable/disable webhook (default: true)
   * @param {string} config.notification_email - Email for webhook issues
   * @returns {Object} Webhook configuration result
   */
  const configureWebhook = async (subaccountId: any, config: SubaccountsFranchises_ConfigurarWebhookInput= {}) => {
    log('useFranchisesSubaccounts', 'configureWebhook', 'Configuring webhook', { subaccountId, config });
    isLoading.value = true;
    error.value = null;

    try {
      validate('SubaccountsFranchises_ConfigurarWebhookInput', config);
      const response = await httpService.post(
        `/index.php/subaccounts/franchises/configurar_webhook/${subaccountId}`,
        config,
        { responseContract: 'SubaccountsFranchises_ConfigurarWebhookResponse' }
      );
      log('useFranchisesSubaccounts', 'configureWebhook', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao configurar webhook');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'configureWebhook', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get webhook status for a subaccount
   *
   * Returns information about whether the webhook is configured,
   * what events it's subscribed to, and if it's enabled.
   *
   * @param {number} subaccountId - Subaccount ID
   * @returns {Object} Webhook status
   *   - configured: boolean - Whether webhook exists
   *   - webhook_id: string - Provider webhook ID
   *   - webhook_url: string - Webhook URL
   *   - events: Array - Subscribed events
   *   - enabled: boolean - Whether webhook is enabled
   *   - interrupted: boolean - Whether webhook is interrupted (too many failures)
   *   - has_auth_token: boolean - Whether auth token is configured
   *   - supports_webhook_config: boolean - Whether provider supports webhook API
   */
  const getWebhookStatus = async (subaccountId: number) => {
    log('useFranchisesSubaccounts', 'getWebhookStatus', 'Fetching webhook status', subaccountId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(
        `/index.php/subaccounts/franchises/webhook_status/${subaccountId}`,
        { responseContract: 'SubaccountsFranchises_WebhookStatusResponse' }
      );
      log('useFranchisesSubaccounts', 'getWebhookStatus', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar status do webhook');
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'getWebhookStatus', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ===========================================================================
  // FRANCHISE SUBACCOUNT STATUS
  // ===========================================================================

  /**
   * Get list of franchise IDs that have active subaccounts configured
   *
   * This is a lightweight method used to determine which franchises
   * can use payment link features.
   *
   * @returns {Promise<number[]>} Array of franchise IDs with active subaccounts
   */
  const getFranchisesWithSubaccounts = async () => {
    log('useFranchisesSubaccounts', 'getFranchisesWithSubaccounts', 'Fetching franchise IDs with subaccounts');

    try {
      const response = await httpService.get('/index.php/subaccounts/franchises/franchises_with_subaccounts', { responseContract: 'SubaccountsFranchises_FranchisesWithSubaccountsResponse' });
      log('useFranchisesSubaccounts', 'getFranchisesWithSubaccounts', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        // Return empty array on error - non-critical, just means no payment links
        log('useFranchisesSubaccounts', 'getFranchisesWithSubaccounts', 'Warning: Could not fetch franchise subaccounts');
        return [];
      }
    } catch (err) {
      log('useFranchisesSubaccounts', 'getFranchisesWithSubaccounts', 'Error', err);
      // Return empty array on error - non-critical
      return [];
    }
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations
    create,
    list,
    getById,
    getByFranchise,
    update,
    remove,

    // Balance & sync
    getBalance,
    sync,

    // Configuration
    getConfig,
    updateConfig,
    updateFiscalConfig,

    // Webhooks
    configureWebhook,
    getWebhookStatus,

    // Franchise subaccount status
    getFranchisesWithSubaccounts
  };
}
